import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  tap,
  throwError,
  firstValueFrom,
} from 'rxjs';
import { User } from '../model/user-interface';
import { Tokens } from '../model/tokens-interface';
import { IS_PUBLIC_API } from '../shared/utils/http-context-tokens';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = environment.apiUrl + '/auth'; // URL del backend
  private currentUser = signal<User | null>(null);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  ); // Para emitir el nuevo token
  private refreshTokenPromise: Promise<Tokens> | null = null; // Para manejar la promesa de refresco
  private http = inject(HttpClient);
  private router = inject(Router);

  constructor() {
    this.loadUserFromStorage();
  }

  // Método para iniciar sesión
  login(email: string, password: string): Observable<Tokens> {
    const context = new HttpContext().set(IS_PUBLIC_API, true);
    return this.http
      .post<Tokens>(`${this.API_URL}/login`, { email, password }, { context })
      .pipe(
        tap((tokens) => {
          this.saveTokens(tokens);
          this.decodeAndSetUser(tokens.access_token);
          this.router.navigate(['/home']); // Redirigir al home después del login
        })
      );
  }

  // Método para refrescar el access token
  refreshToken(): Observable<Tokens> {
    if (this.isRefreshing && this.refreshTokenPromise) {
      // Si ya se está refrescando, devuelve el Observable de la promesa existente
      return new Observable<Tokens>((observer) => {
        this.refreshTokenPromise!.then((tokens) => {
          if (tokens) {
            // Asegurarse de que tokens no sea undefined
            observer.next(tokens);
          }
          observer.complete();
        }).catch((error) => {
          observer.error(error);
        });
      });
    }

    console.log('AuthService: Iniciando refreshToken...');
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null); // Limpiar el subject antes de un nuevo refresco

    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      console.log(
        'AuthService: No hay refresh token disponible. Forzando logout.'
      );
      this.isRefreshing = false;
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    // Convertir a promesa para manejarla con async/await en el interceptor
    this.refreshTokenPromise = firstValueFrom(
      this.http
        .post<Tokens>(`${this.API_URL}/refresh-token`, { refreshToken })
        .pipe(
          tap((tokens) => {
            console.log(
              'AuthService: refreshToken exitoso. Guardando tokens y decodificando usuario.'
            );
            this.saveTokens(tokens);
            this.decodeAndSetUser(tokens.access_token);
            this.isRefreshing = false;
            this.refreshTokenSubject.next(tokens.access_token); // Emitir el nuevo token
            this.refreshTokenPromise = null; // Resetear la promesa
          }),
          catchError((error) => {
            console.log(
              'AuthService: Error en refreshToken. Forzando logout.',
              error
            );
            this.isRefreshing = false;
            this.refreshTokenSubject.error(error); // Emitir error
            this.refreshTokenPromise = null; // Resetear la promesa
            this.logout();
            return throwError(() => error);
          }),
          finalize(() => {
            this.isRefreshing = false;
            this.refreshTokenPromise = null;
          })
        )
    );

    return new Observable<Tokens>((observer) => {
      this.refreshTokenPromise!.then((tokens) => {
        if (tokens) {
          // Asegurarse de que tokens no sea undefined
          observer.next(tokens);
        }
        observer.complete();
      }).catch((error) => {
        observer.error(error);
      });
    });
  }

  // Método para cerrar sesión
  logout(): Observable<any> {
    console.log('AuthService: Iniciando logout...');
    // Cambiado a Observable para manejar la llamada asíncrona
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    const cleanup = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      this.currentUser.set(null);
      this.router.navigate(['/landing']); // O a '/login' si prefieres
    };

    if (accessToken && refreshToken) {
      return this.http
        .post(`${this.API_URL}/logout`, { token: accessToken, refreshToken })
        .pipe(
          catchError((error) => {
            console.error('Error al cerrar sesión en el backend:', error);
            return of(null); // Continuar incluso si hay error en el backend
          }),
          finalize(cleanup) // Limpiar localmente sin importar el resultado del backend
        );
    } else {
      cleanup(); // Limpiar localmente si no hay tokens
      return of(null); // Retornar un observable completado
    }
  }

  // Método para obtener el usuario actual
  getCurrentUser() {
    return this.currentUser.asReadonly();
  }

  // Método para verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    return !!this.currentUser();
  }

  // Método para verificar roles
  hasRole(role: string): boolean {
    return this.currentUser()?.roles.includes(role) || false;
  }

  // Método para verificar permisos
  /* hasPermission(permission: string): boolean {
    return this.currentUser()?.permissions.includes(permission) || false;
  } */

  // Método para obtener el estado de isRefreshing
  getIsRefreshing(): boolean {
    return this.isRefreshing;
  }

  getRefreshTokenSubject(): BehaviorSubject<any> {
    return this.refreshTokenSubject;
  }

  // Método privado para decodificar el token y establecer el usuario
  private decodeAndSetUser(token: string): void {
    //console.log('Token: ', token);

    const payload = JSON.parse(atob(token.split('.')[1]));
    this.currentUser.set({
      id: payload.sub,
      name: payload.nombre,
      email: payload.email,
      avatar: 'assets/calupeavatar.png',
      roles: payload.roles,
      idEmpresa: payload.idEmpresa, // Asumiendo que idEmpresa viene en el payload del JWT
      //permissions: payload.permissions,
    });
  }

  // Método privado para cargar el usuario desde localStorage
  private loadUserFromStorage(): void {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      this.decodeAndSetUser(accessToken);
    }
  }

  // Método privado para guardar los tokens en localStorage
  private saveTokens(tokens: Tokens): void {
    localStorage.setItem('accessToken', tokens.access_token);
    localStorage.setItem('refreshToken', tokens.refresh_token);
  }

  // Método para obtener el access token
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Método para obtener el refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }
}
